import { useEffect, useState } from 'react';

import { clientApi } from '@/config/axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import type { Candidate, SlotOption } from './runtimeContracts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export function PracticeOperations({ base, reviewerId }: { base: string; reviewerId: string }) {
  return (
    <div className="grid gap-6 md:grid-cols-2">
      <ContactPreferences base={base} />
      <Availability base={base} reviewerId={reviewerId} />
    </div>
  );
}
function Availability({ base, reviewerId }: { base: string; reviewerId: string }) {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [visit, setVisit] = useState('Follow-up');
  const [location, setLocation] = useState('');
  const [status, setStatus] = useState('');
  const [slots, setSlots] = useState<SlotOption[]>([]);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    const abort = new AbortController();
    void clientApi
      .get<SlotOption[]>(`${base}/slots`, { signal: abort.signal })
      .then(({ data }) => {
        if (!abort.signal.aborted) setSlots(data);
      })
      .catch(() => {});
    return () => abort.abort();
  }, [base, revision]);
  async function publish() {
    setStatus('Publishing…');
    try {
      await clientApi.post(`${base}/slots`, {
        location,
        visit_type: visit,
        provider_id: reviewerId,
        ends_at: new Date(end).toISOString(),
        starts_at: new Date(start).toISOString(),
      });
      setStatus('Availability published for reviewed bookings.');
      setRevision((value) => value + 1);
    } catch {
      setStatus('Slot was not published. Check the time window and existing availability.');
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Publish my calendar availability</CardTitle>
        <CardDescription>
          MohairNotes checks existing bookings again when an approved task executes. Times use your device’s timezone.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <label>
          Starts
          <Input value={start} type="datetime-local" onChange={(event) => setStart(event.target.value)} />
        </label>
        <label>
          Ends
          <Input value={end} type="datetime-local" onChange={(event) => setEnd(event.target.value)} />
        </label>
        <label>
          Visit type
          <Input value={visit} onChange={(event) => setVisit(event.target.value)} />
        </label>
        <label>
          Location
          <Input value={location} onChange={(event) => setLocation(event.target.value)} />
        </label>
        <Button type="button" disabled={!start || !end || !location || !visit} onClick={() => void publish()}>
          Publish slot
        </Button>
        {slots.map((slot) => (
          <div key={slot.id} className="flex flex-wrap items-center justify-between gap-2">
            <span>
              {new Date(slot.starts_at).toLocaleString()} · {slot.visit_type}
            </span>
            <Button
              size="sm"
              type="button"
              variant="outline"
              onClick={() => {
                void clientApi
                  .delete(`${base}/slots/${slot.id}`)
                  .then(() => setRevision((value) => value + 1))
                  .catch(() => setStatus('Slot could not be withdrawn. It may already be booked.'));
              }}>
              Withdraw slot
            </Button>
          </div>
        ))}
        <p role="status">{status}</p>
      </CardContent>
    </Card>
  );
}
function ContactPreferences({ base }: { base: string }) {
  const [query, setQuery] = useState('');
  const [patients, setPatients] = useState<Candidate[]>([]);
  const [patient, setPatient] = useState('');
  const [email, setEmail] = useState('');
  const [version, setVersion] = useState<number | null>(null);
  const [verified, setVerified] = useState(false);
  const [optedOut, setOptedOut] = useState(true);
  const [status, setStatus] = useState('');
  useEffect(() => {
    setPatients([]);
    setPatient('');
    setVersion(null);
    if (query.trim().length < 2) return;
    const abort = new AbortController();
    const timer = window.setTimeout(() => {
      void clientApi
        .get<Candidate[]>(`${base}/patients`, { params: { q: query }, signal: abort.signal })
        .then(({ data }) => {
          if (!abort.signal.aborted) setPatients(data);
        })
        .catch(() => {});
    }, 300);
    return () => {
      abort.abort();
      window.clearTimeout(timer);
    };
  }, [base, query]);
  useEffect(() => {
    setVersion(null);
    setEmail('');
    setVerified(false);
    setOptedOut(true);
    setStatus('');
    if (!patient) return;
    const abort = new AbortController();
    void clientApi
      .get<{ email: string; version: number; verified: boolean; opted_out: boolean }>(
        `${base}/patients/${patient}/contact`,
        { signal: abort.signal },
      )
      .then(({ data }) => {
        if (!abort.signal.aborted) {
          setEmail(data.email);
          setVersion(data.version);
          setVerified(data.verified);
          setOptedOut(data.opted_out);
        }
      })
      .catch(() => {
        if (!abort.signal.aborted) setStatus('Unable to read contact preferences.');
      });
    return () => abort.abort();
  }, [base, patient]);
  async function save() {
    setStatus('Saving…');
    try {
      const { data } = await clientApi.put<{ version: number }>(`${base}/patients/${patient}/contact`, {
        email,
        verified,
        opted_out: optedOut,
        expected_version: version,
      });
      setVersion(data.version);
      setStatus('Contact preferences saved. Earlier communication drafts must be prepared again.');
    } catch {
      setStatus('Preferences were not saved. Check the email and current version.');
    }
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>Patient communication preferences</CardTitle>
        <CardDescription>
          Verify the destination with the patient and record their communication choice before preparing a message.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <label>
          Find patient
          <Input value={query} onChange={(event) => setQuery(event.target.value)} />
        </label>
        <label>
          Confirm patient
          <select
            value={patient}
            className="mt-2 w-full rounded-md border bg-background p-2"
            onChange={(event) => setPatient(event.target.value)}>
            <option value="">Select patient</option>
            {patients.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} · {p.dob} · MRN {p.mrn}
              </option>
            ))}
          </select>
        </label>
        <label>
          Email
          <Input
            type="email"
            value={email}
            disabled={version === null}
            onChange={(event) => {
              setEmail(event.target.value);
              setVerified(false);
            }}
          />
        </label>
        <label className="flex items-center gap-3">
          <Checkbox
            checked={verified}
            disabled={version === null}
            onCheckedChange={(value) => setVerified(value === true)}
          />
          Destination verified with patient
        </label>
        <label className="flex items-center gap-3">
          <Checkbox
            checked={optedOut}
            disabled={version === null}
            onCheckedChange={(value) => setOptedOut(value === true)}
          />
          Patient opted out of assistant messages
        </label>
        <Button type="button" disabled={version === null || !email} onClick={() => void save()}>
          Save contact preferences
        </Button>
        <p role="status">{status}</p>
      </CardContent>
    </Card>
  );
}
